'use client'

import { useCallback, useMemo, useRef } from 'react'
import { LayoutGroup, motion } from 'motion/react'

import type { ApartmentGalleryViewId } from '@/lib/apartments/apartmentGallery'
import { cn } from '@/utilities/ui'

import { getGalleryViewTabIds, moveGalleryViewTabFocus } from './galleryViewTabs'

const GALLERY_VIEW_TAB_RADIUS_CLASS_NAME = 'rounded-[10px]'
const GALLERY_VIEW_TAB_INNER_RADIUS_CLASS_NAME = 'rounded-[8px]'
const GALLERY_TAB_SPRING_TRANSITION = { type: 'spring' as const, stiffness: 400, damping: 35 }

const galleryTabBarShellClassName = cn(
  'border border-white/10 bg-[rgba(0,33,64,0.80)] p-1 shadow-[0px_6px_20px_0px_rgba(9,114,82,0.12)] backdrop-blur-xl',
  GALLERY_VIEW_TAB_RADIUS_CLASS_NAME,
)

const galleryTabIndicatorClassName = cn(
  'pointer-events-none absolute inset-0 bg-[#8DC8FF] shadow-[0px_6px_20px_0px_rgba(9,114,82,0.12)]',
  GALLERY_VIEW_TAB_INNER_RADIUS_CLASS_NAME,
)

const galleryTabButtonClassName = cn(
  'group relative inline-flex h-9 min-w-0 items-center justify-center px-2.5 font-outfit text-sm font-medium leading-none whitespace-nowrap transition-[background-color,box-shadow,color] duration-300 ease-out motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-turquoise/50 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent sm:px-4',
  GALLERY_VIEW_TAB_INNER_RADIUS_CLASS_NAME,
)

function galleryTabLabelClassName(selected: boolean) {
  return cn(
    'relative z-10 transition-colors duration-300 ease-out motion-reduce:transition-none',
    selected && 'font-semibold text-dark-800 delay-100',
    !selected && 'text-white/80 delay-0 group-hover:text-white group-focus-visible:text-white',
  )
}

type RoomGalleryViewTabsProps = {
  activeView: ApartmentGalleryViewId
  ariaLabel: string
  className?: string
  enabledTabs: ApartmentGalleryViewId[]
  idPrefix: string
  labels: Record<ApartmentGalleryViewId, string>
  onViewChange: (view: ApartmentGalleryViewId) => void
}

export function RoomGalleryViewTabs({
  activeView,
  ariaLabel,
  className,
  enabledTabs,
  idPrefix,
  labels,
  onViewChange,
}: RoomGalleryViewTabsProps) {
  const tabRefs = useRef<Partial<Record<ApartmentGalleryViewId, HTMLButtonElement | null>>>({})
  const tabs = useMemo(
    () => getGalleryViewTabIds().filter((view) => enabledTabs.includes(view)),
    [enabledTabs],
  )
  const indicatorLayoutId = `${idPrefix}-gallery-tab-indicator`
  const layoutGroupId = `${idPrefix}-gallery-tabs`

  const focusTab = useCallback((view: ApartmentGalleryViewId) => {
    tabRefs.current[view]?.focus()
  }, [])

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>, view: ApartmentGalleryViewId) => {
      if (
        event.key !== 'ArrowLeft' &&
        event.key !== 'ArrowRight' &&
        event.key !== 'Home' &&
        event.key !== 'End'
      ) {
        return
      }

      event.preventDefault()
      const nextView = moveGalleryViewTabFocus(enabledTabs, view, event.key)
      onViewChange(nextView)
      focusTab(nextView)
    },
    [enabledTabs, focusTab, onViewChange],
  )

  if (tabs.length <= 1) {
    return null
  }

  return (
    <LayoutGroup id={layoutGroupId}>
      <ul
        aria-label={ariaLabel}
        className={cn(
          'pointer-events-auto flex w-fit min-w-0 max-w-full items-stretch gap-0.5 overflow-hidden',
          galleryTabBarShellClassName,
          className,
        )}
        role="tablist"
      >
        {tabs.map((view) => {
          const selected = activeView === view

          return (
            <li key={view} className="flex min-w-0 flex-[0_1_auto]" role="presentation">
              <button
                ref={(element) => {
                  tabRefs.current[view] = element
                }}
                aria-controls={`${idPrefix}-panel-${view}`}
                aria-selected={selected}
                className={cn(
                  galleryTabButtonClassName,
                  'w-full',
                  !selected && 'hover:bg-white/10 focus-visible:bg-white/10',
                )}
                id={`${idPrefix}-tab-${view}`}
                onClick={() => onViewChange(view)}
                onKeyDown={(event) => handleKeyDown(event, view)}
                role="tab"
                tabIndex={selected ? 0 : -1}
                type="button"
              >
                {selected ? (
                  <motion.div
                    aria-hidden
                    className={galleryTabIndicatorClassName}
                    layoutId={indicatorLayoutId}
                    transition={GALLERY_TAB_SPRING_TRANSITION}
                  />
                ) : null}
                <span className={cn(galleryTabLabelClassName(selected), 'truncate')}>
                  {labels[view]}
                </span>
              </button>
            </li>
          )
        })}
      </ul>
    </LayoutGroup>
  )
}

export const galleryComparisonLabelClassName =
  'rounded-full bg-black/70 px-3 py-1 font-outfit text-sm font-medium text-white'

export const galleryOverlayControlClassName = cn(
  'pointer-events-auto flex size-11 items-center justify-center border border-white/10 bg-[rgba(0,33,64,0.80)] text-white/95 shadow-[0px_6px_20px_0px_rgba(9,114,82,0.12)] backdrop-blur-xl transition-colors',
  GALLERY_VIEW_TAB_RADIUS_CLASS_NAME,
  'hover:bg-[rgba(0,33,64,0.88)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-turquoise/50 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent',
)
